import { useCallback, useEffect, useState } from "react";
import AdminAuthGuard from "../components/admin/AdminAuthGuard";
import AdminLayout from "../components/admin/AdminLayout";
import {
  listAdminImplementationRequests,
  patchImplementationRequest,
} from "../lib/api/index";
import { logger } from "../lib/logger";

const STATUSES = ["new", "contacted", "scheduled", "won", "lost"];

const statusPillStyle = (status) => {
  const palette = {
    new:        { fg: "#9aa1aa", bg: "rgba(154,161,170,0.10)", border: "#3a4049" },
    contacted:  { fg: "#c9a24a", bg: "rgba(201,162,74,0.12)", border: "#7a6128" },
    scheduled:  { fg: "#6da9ff", bg: "rgba(109,169,255,0.12)", border: "#3863a5" },
    won:        { fg: "#7adb8e", bg: "rgba(122,219,142,0.12)", border: "#3f8c4f" },
    lost:       { fg: "#e2867e", bg: "rgba(226,134,126,0.12)", border: "#8a3e3a" },
  }[status] || { fg: "#9aa1aa", bg: "rgba(154,161,170,0.10)", border: "#3a4049" };
  return {
    color: palette.fg,
    backgroundColor: palette.bg,
    border: `1px solid ${palette.border}`,
    padding: "2px 8px",
    borderRadius: 12,
    fontFamily: "var(--sf-mono)",
    fontSize: 11,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    display: "inline-block",
  };
};

function Modal({ open, onClose, request, onSubmitted }) {
  const [status, setStatus] = useState(request?.status || "new");
  const [notes, setNotes] = useState(request?.internal_notes || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open && request) {
      setStatus(request.status || "new");
      setNotes(request.internal_notes || "");
      setErr(null);
    }
  }, [open, request]);

  if (!open || !request) return null;

  const onSave = async () => {
    setSaving(true); setErr(null);
    try {
      const data = await patchImplementationRequest(request.request_id, {
        status, internal_notes: notes,
      });
      onSubmitted(data);
    } catch (e) {
      logger.error("admin_impl_update_failed", { status: e?.response?.status });
      setErr("Could not save changes.");
    } finally { setSaving(false); }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="admin-impl-modal"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="sf-card"
        style={{ maxWidth: 560, width: "100%" }}
      >
        <div className="sf-eyebrow" style={{ marginBottom: 8 }}>Implementation request</div>
        <h2 className="sf-display" style={{ fontSize: 20, marginBottom: 4 }}>{request.business}</h2>
        <div className="sf-mono sf-muted-text" style={{ fontSize: 12, marginBottom: 18 }}>
          {request.name} · {request.email} · {request.preferred_tier}
        </div>

        <div className="sf-eyebrow" style={{ marginBottom: 10 }}>Status</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }} data-testid="admin-impl-status-radios">
          {STATUSES.map((s) => (
            <label key={s} style={{ cursor: "pointer" }}>
              <input
                type="radio"
                name="impl-status"
                value={s}
                checked={status === s}
                onChange={() => setStatus(s)}
                style={{ marginRight: 6 }}
                data-testid={`admin-impl-status-${s}`}
              />
              <span style={statusPillStyle(s)}>{s}</span>
            </label>
          ))}
        </div>

        <div className="sf-eyebrow" style={{ marginBottom: 10 }}>Internal notes</div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={5}
          className="sf-input"
          style={{ width: "100%", marginBottom: 14, fontFamily: "var(--sf-mono)", fontSize: 13 }}
          data-testid="admin-impl-notes"
        />

        {request.notes && (
          <div style={{ marginBottom: 14, fontSize: 13 }}>
            <div className="sf-eyebrow" style={{ marginBottom: 4 }}>From the customer</div>
            <div className="sf-muted-text">{request.notes}</div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" className="sf-btn-secondary" onClick={onClose} data-testid="admin-impl-cancel">
            Cancel
          </button>
          <button type="button" className="sf-btn" onClick={onSave} disabled={saving} data-testid="admin-impl-save">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>

        {err && <div className="sf-mono" style={{ color: "#e2867e", fontSize: 12, marginTop: 10 }} data-testid="admin-impl-modal-error">{err}</div>}
      </div>
    </div>
  );
}

function Inner({ admin }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(null);

  const fetchPage = useCallback(async (p, s) => {
    setLoading(true); setErr(null);
    try {
      const params = { page: p, page_size: pageSize };
      if (s) params.status_filter = s;
      const data = await listAdminImplementationRequests(params);
      setRows(data.rows);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (e) {
      logger.error("admin_impl_list_failed", { status: e?.response?.status });
      setErr("Could not load implementation requests.");
    } finally { setLoading(false); }
  }, [pageSize]);

  useEffect(() => { fetchPage(page, statusFilter); }, [fetchPage, page, statusFilter]);

  const onSubmitted = (updated) => {
    setRows((rs) => rs.map((r) => r.request_id === updated.request_id ? { ...r, ...updated } : r));
    setActive(null);
  };

  return (
    <AdminLayout admin={admin}>
      <div className="sf-eyebrow" style={{ marginBottom: 8 }}>Implementation requests</div>
      <h1 className="sf-display" style={{ fontSize: 28, marginBottom: 18 }} data-testid="admin-impl-headline">
        Implementation pipeline.
      </h1>

      <div className="sf-card" style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }} data-testid="admin-impl-filters">
        <label className="sf-eyebrow" style={{ fontSize: 11 }}>Status
          <select value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
                  className="sf-input" style={{ marginLeft: 8 }} data-testid="impl-filter-status">
            <option value="">all</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <span className="sf-mono sf-muted-text" style={{ marginLeft: "auto", fontSize: 12 }} data-testid="impl-total">
          {total} total
        </span>
      </div>

      {err && <div className="sf-card" data-testid="admin-impl-error">{err}</div>}

      <div className="sf-card" style={{ padding: 0 }} data-testid="admin-impl-table">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--sf-gold)", fontFamily: "var(--sf-mono)", fontSize: 11, letterSpacing: "0.08em" }}>
              <th style={{ padding: 10 }}>BUSINESS</th>
              <th style={{ padding: 10 }}>CONTACT</th>
              <th style={{ padding: 10 }}>TIER</th>
              <th style={{ padding: 10 }}>STATUS</th>
              <th style={{ padding: 10 }}>CREATED</th>
              <th style={{ padding: 10 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ padding: 16, color: "var(--sf-muted)" }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} style={{ padding: 16, color: "var(--sf-muted)" }}>No implementation requests yet.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.request_id} style={{ borderTop: "1px solid #22262B" }} data-testid={`admin-impl-row-${r.request_id}`}>
                <td style={{ padding: 10 }}>{r.business}</td>
                <td style={{ padding: 10 }} className="sf-mono">{r.name} · {r.email}</td>
                <td style={{ padding: 10 }} className="sf-mono">{r.preferred_tier}</td>
                <td style={{ padding: 10 }}><span style={statusPillStyle(r.status)} data-testid={`admin-impl-row-status-${r.request_id}`}>{r.status}</span></td>
                <td style={{ padding: 10 }} className="sf-mono sf-muted-text">{r.created_at}</td>
                <td style={{ padding: 10, textAlign: "right" }}>
                  <button type="button" className="sf-btn-secondary" onClick={() => setActive(r)} data-testid={`admin-impl-row-edit-${r.request_id}`}>
                    Update
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "center", justifyContent: "flex-end" }} data-testid="admin-impl-pager">
        <button type="button" className="sf-btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="impl-page-prev">← Prev</button>
        <span className="sf-mono sf-muted-text" style={{ fontSize: 12 }}>Page {page} / {totalPages}</span>
        <button type="button" className="sf-btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="impl-page-next">Next →</button>
      </div>

      <Modal open={!!active} onClose={() => setActive(null)} request={active} onSubmitted={onSubmitted} />
    </AdminLayout>
  );
}

export default function AdminImplementationRequests() {
  return <AdminAuthGuard>{(admin) => <Inner admin={admin} />}</AdminAuthGuard>;
}
